import { Wallet } from 'lucide-react';
import { OrgAdminEmptyState, OrgAdminTableSkeleton } from '@/components/molecules/org-admin/OrgAdminStates';
import { PlainHeader } from '@/components/molecules/org-admin/OrgAdminTable';
import type { OrgCreditEntry } from '@/services/org-admin';
import { formatCurrencyUsd } from './usageFormat';

/**
 * The org's full credit-pool ledger — every purchase and every assignment, newest first.
 *
 * Replaces the single "Last movement" line that used to be the only visible history: a balance
 * with no trail is a number an admin has to take on trust, and "last movement" answers "what just
 * happened" but not "where did this $305 actually come from and go to" once there is more than one
 * entry.
 *
 * There is no Stripe invoice/receipt link on a purchase row, unlike `InvoicesPanel`. A credit
 * purchase is a one-time PaymentIntent/Checkout charge, which Stripe does not turn into an Invoice
 * object the way a subscription renewal does — so there is no Stripe-hosted document to link to.
 * This row (amount paid, bonus applied, when) IS the receipt.
 */

const DATE = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

function formatDate(iso: string): string {
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? '—' : DATE.format(new Date(parsed));
}

function movementDescription(entry: OrgCreditEntry): string {
  if (entry.kind === 'purchase') return 'Purchased';
  return entry.assigned_to_email ? `Assigned to ${entry.assigned_to_email}` : 'Assigned';
}

export interface CreditLedgerTableProps {
  entries: OrgCreditEntry[] | undefined;
  isLoading: boolean;
}

export function CreditLedgerTable({ entries, isLoading }: CreditLedgerTableProps) {
  if (isLoading) {
    return (
      <OrgAdminTableSkeleton
        rows={3}
        columns={['Date', 'Movement', 'Amount', 'Detail']}
        label="Loading credit history"
      />
    );
  }

  if (!entries?.length) {
    return (
      <OrgAdminEmptyState
        icon={Wallet}
        title="No credit history yet"
        description="Purchases and assignments appear here once this organization buys credit."
        className="min-h-[120px]"
      />
    );
  }

  return (
    <table className="oa-table">
      <caption className="sr-only">Credit purchases and assignments, newest first</caption>
      <thead>
        <tr>
          <PlainHeader label="Date" />
          <PlainHeader label="Movement" />
          <PlainHeader label="Amount" align="right" />
          <PlainHeader label="Detail" />
        </tr>
      </thead>
      <tbody>
        {entries.map((entry) => (
          <tr key={entry.id}>
            <td className="truncate">{formatDate(entry.inserted_at)}</td>
            <td className="truncate">{movementDescription(entry)}</td>
            <td className="oa-num">
              {entry.kind === 'purchase' ? '+' : ''}
              {formatCurrencyUsd(entry.amount_usd)}
            </td>
            <td className="truncate text-muted-foreground">
              {/* Only a purchase row carries a paid amount and bonus rate — an assignment moves
                  already-bought credit, so there is nothing else to show for it. */}
              {entry.kind === 'purchase' && entry.amount_paid_usd !== null
                ? `Paid ${formatCurrencyUsd(entry.amount_paid_usd)}${
                    entry.bonus_multiplier ? ` at ${entry.bonus_multiplier.toFixed(1)}×` : ''
                  }`
                : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
